import { useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import { useTranslation } from '../../hooks/useTranslation';
import Api from '../../utils/api';

export default function useDropRegistration({ initial, code, returnTo }) {
  const { t, locale } = useTranslation('drop');
  const context = useContext(GlobalContext);
  const [drop, setDrop] = useState(initial || null);
  const [notes, setNotes] = useState('');
  const [checking, setChecking] = useState(!!code);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const destination = returnTo || `${locale === 'en' ? '/en' : ''}/drop/${code}`;
  const loginHref = `/cont/login?redirect=${encodeURIComponent(destination)}`;
  const viewer = drop?.viewer || {};
  const authenticated = viewer.authenticated || !!context?.user;
  const deadlinePassed = !!drop?.registrationEndsAt && new Date(drop.registrationEndsAt) <= new Date();
  const open = !!drop?.open && !deadlinePassed;

  // Always refresh through the browser's session, just like the registration POST.
  // A failed refresh has an explicit retry; never fall through to add-to-cart.
  useEffect(() => {
    if (!code) return undefined;
    let alive = true;
    setChecking(true);
    setError(false);
    Api.get('/v2/drops/public/[code]', { code })
      .then((response) => {
        if (!response?.data?.viewer) throw new Error('invalid_drop');
        if (alive) setDrop(response.data);
      })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [code, context?.user?.id, attempt]);

  const patchViewer = (patch) => setDrop((value) => ({ ...value, viewer: { ...value.viewer, ...patch } }));
  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || checking || error || !open || viewer.registered || !authenticated || (drop.access === 'discord' && !viewer.discordLinked)) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await Api.post('/v2/drops/public/[code]/register', { code }, null, { notes: notes.trim() || null });
      patchViewer({ registered: true });
      toast.success(t('successToast'));
    } catch (failure) {
      const reason = failure?.message || failure?.code || failure;
      if (reason === 'already_registered') patchViewer({ registered: true });
      else if (reason === 'drop_closed') setDrop((value) => ({ ...value, open: false }));
      else if (reason === 'discord_required') { patchViewer({ discordLinked: false }); toast.error(t('discordRequired')); }
      else if (reason === 'unauthenticated') window.location.href = loginHref;
      else toast.error(t('errorGeneric'));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return { drop, viewer, notes, setNotes, checking, error, retry: () => setAttempt((value) => value + 1),
    submitting, authenticated, open, deadlinePassed, loginHref, submit };
}
